/**
 * <igf-hero> — cross-fading hero slider.
 *
 * Mirrors the reference behaviour: fade transition, infinite wrap, autoplay
 * that pauses permanently once the visitor interacts, and arrows only.
 * No dependencies.
 */
class IgfHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.igf-hero__slide'));
    if (this.slides.length === 0) return;

    this.index = Math.max(
      0,
      this.slides.findIndex((s) => s.classList.contains('is-active'))
    );
    this.interval = Number(this.dataset.autoplaySpeed) || 5000;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;

    this.querySelector('[data-igf-hero-prev]')?.addEventListener('click', () => {
      this.stop();
      this.go(this.index - 1);
    });

    this.querySelector('[data-igf-hero-next]')?.addEventListener('click', () => {
      this.stop();
      this.go(this.index + 1);
    });

    // Pause on interaction, matching the reference's pause_on_interaction.
    ['pointerdown', 'focusin'].forEach((evt) => this.addEventListener(evt, () => this.stop()));

    this.onVisibility = () => (document.hidden ? this.pause() : this.resume());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.go(this.index);
    this.resume();
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  go(next) {
    const total = this.slides.length;
    this.index = ((next % total) + total) % total; // wraps both directions

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      // Keep hidden slides out of the tab order.
      slide.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });
  }

  resume() {
    if (!this.autoplay || this.timer) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }

  /** Permanent stop — the visitor has taken over. */
  stop() {
    this.pause();
    this.autoplay = false;
  }
}

customElements.define('igf-hero', IgfHero);
